import { OFFSCREEN_CONFIG, INDICATOR_DIRECTIONS } from '@/constants/offscreen.constants';

/**
 * Offscreen indicators business logic service
 */
export class OffscreenService {
  /**
   * Check if indicator should be visible based on count
   * @param {number} count - Number of offscreen items
   * @returns {boolean}
   */
  static shouldShowIndicator(count) {
    if (typeof count !== 'number' || count < 0) return false;
    return !OFFSCREEN_CONFIG.VISIBILITY.HIDDEN_STATES.includes(count);
  }

  /**
   * Validate offscreen counts input
   * @param {object} counts - Offscreen counts object
   * @returns {boolean} Whether input is valid
   */
  static validateOffscreenCounts(counts) {
    if (!counts || typeof counts !== 'object') return false;

    const requiredProps = ['offscreenTop', 'offscreenBottom', 'offscreenLeft', 'offscreenRight'];
    return requiredProps.every(prop => 
      Object.prototype.hasOwnProperty.call(counts, prop) && 
      typeof counts[prop] === 'number' && 
      counts[prop] >= 0
    );
  }

  /**
   * Calculate total offscreen items
   * @param {object} offscreenCounts - Object with direction counts
   * @returns {number} Total count
   */
  static getTotalOffscreenCount(offscreenCounts) {
    if (!this.validateOffscreenCounts(offscreenCounts)) return 0;
    return Object.values(offscreenCounts).reduce((total, count) => total + count, 0);
  }

  /**
   * Get the direction with the most offscreen items
   * @param {object} offscreenCounts - Object with direction counts
   * @returns {string|null} Direction with highest count
   */
  static getPrimaryDirection(offscreenCounts) {
    if (!this.validateOffscreenCounts(offscreenCounts)) return null;

    let maxCount = 0;
    let primaryDirection = null;

    INDICATOR_DIRECTIONS.forEach(direction => {
      const prop = `offscreen${direction.charAt(0).toUpperCase() + direction.slice(1)}`;
      const count = offscreenCounts[prop] || 0;
      
      if (count > maxCount) {
        maxCount = count;
        primaryDirection = direction;
      }
    });

    return primaryDirection;
  }
}